import { skipToken } from "@reduxjs/toolkit/query/react";
import useGetCurrentUserDocument from "./useGetCurrentUserDocument";
import {
  useGetCartsByUserQuery,
  useRemoveCartItemMutation,
  useAddCartItemMutation,
  useGetProductsQuery,
  useUpdateCartItemMutation,
} from "../api/payments";
import { ProductType } from "../../../api/src/types";

export const useCartAndUser = () => {
  // User, Document, Carts, Products
  const {
    user,
    document,
    isLoading: userDocLoading,
    error: userDocError,
  } = useGetCurrentUserDocument();

  const {
    data: carts,
    isLoading: isCartsLoading,
    error: cartsError,
    refetch: refetchCarts,
  } = useGetCartsByUserQuery(
    user?.id ? { id: user.id, transactionStatus: "pending" } : skipToken,
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const {
    data: products,
    isLoading: isProductsLoading,
    error: isProductsError,
  } = useGetProductsQuery();

  // Cart item actions
  const [
    removeCartItem,
    {
      isLoading: isRemoveCartItemLoading,
      isError: isRemoveCartItemError,
      reset: resetRemoveCartItem,
    },
  ] = useRemoveCartItemMutation();
  const [
    addCartItem,
    {
      isLoading: isAddCartItemLoading,
      isError: isAddCartItemError,
      reset: resetAddCartItem,
    },
  ] = useAddCartItemMutation();
  const [
    updateCartItem,
    {
      isLoading: isUpdateCartItemLoading,
      isError: isUpdateCartItemError,
      reset: resetUpdateCartItem,
    },
  ] = useUpdateCartItemMutation();

  // Products currently in user's shopping cart i.e. Active cart- transactionStatus: pending
  const isPrenupInCart = carts?.[0]?.cartItems?.some(
    (item) =>
      item.product.productType === ProductType.PRENUP &&
      item.deletedAt === null,
  );
  const isNotarizationInCart = carts?.[0]?.cartItems?.some(
    (item) =>
      item.product.productType === ProductType.NOTARIZATION &&
      item.deletedAt === null,
  );
  const isRepresentationInCart = carts?.[0]?.cartItems?.some(
    (item) =>
      item.product.productType === ProductType.REPRESENTATION &&
      item.deletedAt === null,
  );
  const isQaInCart = carts?.[0]?.cartItems?.some(
    (item) =>
      item.product.productType === ProductType.Q_A && item.deletedAt === null,
  );
  const hasDeletedNotarizationInCart = carts?.[0]?.cartItems?.some(
    (item) =>
      item.product.productType === ProductType.NOTARIZATION &&
      item.deletedAt !== null,
  );

  //Cart and User loading and error
  const isUserCartsLoading = userDocLoading || isCartsLoading;
  const isUserCartsError = cartsError ?? userDocError;
  const cartItems = carts?.[0]?.cartItems ?? [];

  // Cart item actions
  const isCartItemActionLoading =
    isAddCartItemLoading || isUpdateCartItemLoading || isRemoveCartItemLoading;
  const isCartItemActionError =
    isAddCartItemError || isUpdateCartItemError || isRemoveCartItemError;

  // Helper function to determine error type
  const getErrorType = () => {
    if (isAddCartItemError) return "add";
    if (isUpdateCartItemError) return "update";
    if (isRemoveCartItemError) return "remove";
    return null;
  };

  const isOnlineNotaryEnabled =
    !!document?.state && !!document.state.allowsOnlineNotary;

  return {
    user,
    document,
    carts,
    refetchCarts,
    cartItems,
    isUserCartsLoading,
    isUserCartsError,
    addCartItem,
    updateCartItem,
    removeCartItem,
    isCartItemActionLoading,
    isCartItemActionError,
    isOnlineNotaryEnabled,
    // Individual error flags
    isAddCartItemError,
    isUpdateCartItemError,
    isRemoveCartItemError,
    // Error type helper
    getErrorType,
    resetRemoveCartItem,
    resetAddCartItem,
    resetUpdateCartItem,
    products,
    isProductsLoading,
    isProductsError,
    // Products in user's active cart
    isPrenupInCart,
    isNotarizationInCart,
    isRepresentationInCart,
    isQaInCart,
    hasDeletedNotarizationInCart,
  };
};
